import { useCallback, useMemo, type ReactNode } from 'react';
import {
  Modal,
  Tag,
  Typography,
  Spin,
  Alert,
  Collapse,
  theme,
  Button,
  Flex,
  Avatar,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  CopyOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { usePaymentGatewayStatusQuery } from '../api';
import type { Payment } from '../api';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formModalWidth } from '@/components/ui/formModalStyles';

const { Text } = Typography;
const { useToken } = theme;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CheckGatewayStatusModalProps {
  open: boolean;
  onClose: () => void;
  payment: Payment | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parses a compact gateway timestamp (e.g. "20260205120105") into a
 * human-readable format ("05 Feb 2026, 12:01:05"). Falls back to the
 * raw string if parsing fails.
 */
const formatTimestamp = (timestamp: string | undefined): string => {
  if (!timestamp) return 'N/A';
  const parsed = dayjs(timestamp, 'YYYYMMDDHHmmss');
  return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm:ss') : timestamp;
};

/** Copies a string to the clipboard (fire-and-forget). */
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Labelled key-value pair with optional copy button and monospace styling. */
const DetailItem = ({
  label,
  value,
  copyable = false,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  copyable?: boolean;
  mono?: boolean;
}) => (
  <div>
    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
      {label}
    </Text>
    <Flex align="center" gap={4}>
      <Text strong style={{ fontSize: 13, fontFamily: mono ? 'monospace' : undefined }}>
        {value || <Text type="secondary">N/A</Text>}
      </Text>
      {copyable && value && (
        <Tooltip title="Copy">
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined style={{ fontSize: 11 }} />}
            onClick={() => copyToClipboard(String(value))}
            style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
          />
        </Tooltip>
      )}
    </Flex>
  </div>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Read-only modal that fetches and displays the real-time gateway status
 * for a given payment token.
 *
 * Sections:
 * 1. **Status banner** — success / failure with response code.
 * 2. **Details grid** — gateway, amount, and action.
 * 3. **Timestamp** — when the transaction was processed.
 * 4. **Authorization codes** — approval code, RRN, internal reference (when available).
 * 5. **Token** — the full payment intent token with a copy button.
 * 6. **Raw response** — collapsible JSON dump for debugging.
 *
 * Includes a "Refresh" button to re-query the gateway on demand.
 */
export function CheckGatewayStatusModal({
  open,
  onClose,
  payment,
}: CheckGatewayStatusModalProps) {
  const { token } = useToken();
  const { t } = useTranslation('payment-module');

  const {
    data: gatewayStatus,
    isLoading,
    error,
    refetch,
  } = usePaymentGatewayStatusQuery(open ? payment?.token : null);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  /** Refreshes the gateway status query. */
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // -------------------------------------------------------------------------
  // Memoised styles (depend on the theme token and gateway result)
  // -------------------------------------------------------------------------

  const statusBannerStyle = useMemo(() => {
    if (!gatewayStatus) return {};
    return {
      background: gatewayStatus.is_successful
        ? token.colorSuccessBg
        : token.colorErrorBg,
      borderRadius: token.borderRadius,
      padding: `${token.paddingSM}px ${token.paddingMD}px`,
      marginBottom: token.marginMD,
    };
  }, [gatewayStatus, token]);

  const timestampRowStyle = useMemo(() => ({
    marginTop: token.marginSM,
    padding: `${token.paddingXS}px ${token.paddingSM}px`,
    background: token.colorBgLayout,
    borderRadius: token.borderRadiusSM,
  }), [token]);

  const tokenDisplayStyle = useMemo(() => ({
    background: token.colorBgLayout,
    borderRadius: token.borderRadiusSM,
    padding: `${token.paddingXS}px ${token.paddingSM}px`,
    fontFamily: 'monospace',
    fontSize: 11,
    wordBreak: 'break-all' as const,
    color: token.colorTextSecondary,
  }), [token]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Modal
      title={
        <Flex align="center" gap={token.marginXS}>
          <Avatar
            size={28}
            style={{
              backgroundColor: token.colorPrimaryBg,
              color: token.colorPrimary,
            }}
            icon={<BankOutlined />}
          />
          <Text strong>{t('gatewayModal.title')}</Text>
        </Flex>
      }
      open={open}
      onCancel={onClose}
      footer={
        <Flex justify="space-between">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {t('gatewayModal.refresh')}
          </Button>
          <Button type="primary" onClick={onClose}>
            {t('gatewayModal.close')}
          </Button>
        </Flex>
      }
      width={formModalWidth(560)}
      destroyOnHidden
    >
      {/* Loading state */}
      {isLoading && (
        <Flex vertical align="center" justify="center" style={{ padding: token.paddingLG * 2 }}>
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: token.marginMD }}>
            {t('gatewayModal.loading')}
          </Text>
        </Flex>
      )}

      {/* Error state with retry */}
      {error && (
        <Alert
          title={t('gatewayModal.errorTitle')}
          description={error instanceof Error ? error.message : 'Unknown error occurred'}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleRefresh}>
              {t('gatewayModal.retry')}
            </Button>
          }
        />
      )}

      {/* Gateway response */}
      {gatewayStatus && !isLoading && (
        <div>
          {/* Success / failure banner with response code */}
          <div style={statusBannerStyle}>
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={token.marginSM}>
                <Avatar
                  size={36}
                  style={{
                    backgroundColor: gatewayStatus.is_successful ? token.colorSuccess : token.colorError,
                  }}
                  icon={gatewayStatus.is_successful ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                />
                <div>
                  <Text strong style={{ fontSize: 15, display: 'block' }}>
                    {gatewayStatus.is_successful ? t('gatewayModal.transactionSuccess') : t('gatewayModal.transactionFailed')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {gatewayStatus.message || t('gatewayModal.noMessage')}
                  </Text>
                </div>
              </Flex>
              <Tag
                color={gatewayStatus.is_successful ? 'success' : 'error'}
                style={{ margin: 0 }}
              >
                {t('gatewayModal.code')}: {gatewayStatus.response_code || 'N/A'}
              </Tag>
            </Flex>
          </div>

          {/* Gateway, amount, and action details */}
          <Row gutter={[token.marginMD, token.marginSM]}>
            <Col span={8}>
              <DetailItem
                label={t('gatewayModal.gatewayLabel')}
                value={<Tag color="purple" style={{ margin: 0 }}>{gatewayStatus.gateway?.toUpperCase()}</Tag>}
              />
            </Col>
            <Col span={8}>
              <DetailItem
                label={t('gatewayModal.amount')}
                value={gatewayStatus.amount ? `${gatewayStatus.amount} ${gatewayStatus.currency}` : null}
              />
            </Col>
            <Col span={8}>
              <DetailItem
                label={t('gatewayModal.action')}
                value={gatewayStatus.action}
                mono
              />
            </Col>
          </Row>

          {/* Transaction timestamp */}
          <div style={timestampRowStyle}>
            <Flex align="center" gap={token.marginXS}>
              <ClockCircleOutlined style={{ color: token.colorTextSecondary, fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatTimestamp(gatewayStatus.timestamp)}
              </Text>
            </Flex>
          </div>

          {/* Authorization codes (only shown when at least one is present) */}
          {(gatewayStatus.approval_code || gatewayStatus.rrn || gatewayStatus.int_ref) && (
            <div style={{ marginTop: token.marginMD }}>
              <Text type="secondary" style={{ fontSize: 11, marginBottom: token.marginXS, display: 'block' }}>
                {t('gatewayModal.authorization')}
              </Text>
              <div
                style={{
                  background: token.colorBgLayout,
                  borderRadius: token.borderRadius,
                  padding: token.paddingSM,
                }}
              >
                <Row gutter={[token.marginMD, token.marginXS]}>
                  {gatewayStatus.approval_code && (
                    <Col span={8}>
                      <DetailItem label={t('gatewayModal.approval')} value={gatewayStatus.approval_code} copyable mono />
                    </Col>
                  )}
                  {gatewayStatus.rrn && (
                    <Col span={8}>
                      <DetailItem label={t('gatewayModal.rrn')} value={gatewayStatus.rrn} copyable mono />
                    </Col>
                  )}
                  {gatewayStatus.int_ref && (
                    <Col span={8}>
                      <DetailItem label={t('gatewayModal.intRef')} value={gatewayStatus.int_ref} copyable mono />
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          )}

          {/* Payment intent token with copy action */}
          <div style={{ marginTop: token.marginMD }}>
            <Flex align="center" justify="space-between">
              <Text type="secondary" style={{ fontSize: 11 }}>{t('gatewayModal.token')}</Text>
              <Tooltip title={t('gatewayModal.copyToken')}>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined style={{ fontSize: 11 }} />}
                  onClick={() => copyToClipboard(gatewayStatus.payment_intent_token)}
                >
                  {t('gatewayModal.copy')}
                </Button>
              </Tooltip>
            </Flex>
            <div style={tokenDisplayStyle}>
              {gatewayStatus.payment_intent_token}
            </div>
          </div>

          {/* Collapsible raw gateway response for debugging */}
          <Collapse
            ghost
            size="small"
            style={{ marginTop: token.marginSM }}
            items={[
              {
                key: 'raw-response',
                label: (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {t('gatewayModal.rawResponse')}
                  </Text>
                ),
                children: (
                  <div
                    style={{
                      background: token.colorBgLayout,
                      borderRadius: token.borderRadiusSM,
                      padding: token.paddingSM,
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: 10, fontFamily: 'monospace' }}>
                      {JSON.stringify(gatewayStatus.response_data, null, 2)}
                    </pre>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
}
